import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { z } from "zod";
import { useForm } from "@core/features/form";
import {
  useFetchOrganization,
  useUpdateOrganization,
} from "@core/features/organization";
import { socialUrlField } from "@core/schemas/fields";
import { useStepFormSync } from "@platform/features/onboarding";
import type { StepValidationState } from "@platform/features/onboarding";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrgSocialsStep } from "./OrgSocialsStep";

const schema = z.object({
  instagram_url: socialUrlField,
  facebook_url: socialUrlField,
  tiktok_url: socialUrlField,
  bluesky_url: socialUrlField,
  linkedin_url: socialUrlField,
});

export type Props = {
  programId: number;
  organizationId: number;
  onComplete: () => void;
  onSkip?: () => void;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
};

/**
 * Org socials sub-step container.
 * Allows admin to update the organization's social media URLs.
 */
export function OrgSocialsStepContainer({
  organizationId,
  onComplete,
  onStepStateChange,
  submitRef,
}: Props) {
  const { data: organization, isLoading } = useFetchOrganization({
    organizationId,
    enabled: !!organizationId,
  });

  const mutation = useUpdateOrganization({
    organizationId,
    onSuccess: () => {
      onComplete();
    },
  });

  const defaultValues = useMemo(
    () => ({
      instagram_url: organization?.instagram_url ?? "",
      facebook_url: organization?.facebook_url ?? "",
      tiktok_url: organization?.tiktok_url ?? "",
      bluesky_url: organization?.bluesky_url ?? "",
      linkedin_url: organization?.linkedin_url ?? "",
    }),
    [organization]
  );

  const { form, handleSubmit } = useForm({
    schema,
    defaultValues,
    onSubmit: mutation.mutateAsync,
  });

  const hasInitialDataLoaded = useRef(false);

  useEffect(() => {
    if (organization && !hasInitialDataLoaded.current) {
      hasInitialDataLoaded.current = true;
      form.reset({
        instagram_url: organization.instagram_url ?? "",
        facebook_url: organization.facebook_url ?? "",
        tiktok_url: organization.tiktok_url ?? "",
        bluesky_url: organization.bluesky_url ?? "",
        linkedin_url: organization.linkedin_url ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const values = form.watch();
  const hasAnySocial = Object.values(values).some(
    (v) => typeof v === "string" && v.trim().length > 0
  );

  useStepFormSync({
    shouldSync: true,
    onStepStateChange,
    submitRef,
    formState: form.formState,
    onSubmit: handleSubmit,
    canProceed: hasAnySocial && form.formState.isValid && !mutation.isPending,
  });

  if (isLoading || !organization) {
    return <Loader isFullPage />;
  }

  return <OrgSocialsStep form={form} />;
}

export default OrgSocialsStepContainer;
